type AccentTextColorFields = {
  className: string;
  hex: string;
  title: string;
  value: string;
};

/**
 * Named brand colors for the accent text mark.
 *
 * TODO: Create a single source of truth for these tokens in
 * `@homeinstead/tailwind-config` and derive this catalog from it. The hex
 * values are copied from `theme.extend.colors` because that export mixes
 * brand colors with semantic tokens and pulls in Tailwind plugins, which
 * `@homeinstead/schemas` does not depend on.
 *
 * Ramp colors use the `-base` utility. Flat tokens (`holiday`, `peace`,
 * `serenity`) have no `base` key, so their utility is the token name.
 * Accent fill reads `hex` through `--accent-color` so the letter animation
 * can target the accent without applying a Tailwind color class that would
 * block inheriting the heading color before fill.
 */
const ACCENT_TEXT_COLOR_DEFINITIONS = [
  {
    className: "text-brick-base",
    hex: "#662a12",
    title: "Brick",
    value: "brick",
  },
  {
    className: "text-bright-future-base",
    hex: "#faac6e",
    title: "Bright Future",
    value: "bright-future",
  },
  {
    className: "text-clear-sky-base",
    hex: "#a6d9ff",
    title: "Clear Sky",
    value: "clear-sky",
  },
  {
    className: "text-friendly-base",
    hex: "#deed6a",
    title: "Friendly",
    value: "friendly",
  },
  {
    className: "text-holiday",
    hex: "#9f2523",
    title: "Holiday",
    value: "holiday",
  },
  {
    className: "text-new-beginnings-base",
    hex: "#2c4f26",
    title: "New Beginnings",
    value: "new-beginnings",
  },
  {
    className: "text-night-sky-base",
    hex: "#203C56",
    title: "Night Sky",
    value: "night-sky",
  },
  {
    className: "text-peace",
    hex: "#3c3760",
    title: "Peace",
    value: "peace",
  },
  {
    className: "text-serenity",
    hex: "#b7b4ee",
    title: "Serenity",
    value: "serenity",
  },
  {
    className: "text-support-base",
    hex: "#f6ffe3",
    title: "Support",
    value: "support",
  },
] as const satisfies readonly AccentTextColorFields[];

type AccentTextColor = (typeof ACCENT_TEXT_COLOR_DEFINITIONS)[number]["value"];

const ACCENT_TEXT_COLOR_OPTIONS = ACCENT_TEXT_COLOR_DEFINITIONS.map(
  ({ title, value }) => ({ title, value }),
);

function recordByAccentColor(
  select: (
    definition: (typeof ACCENT_TEXT_COLOR_DEFINITIONS)[number],
  ) => string,
): Record<AccentTextColor, string> {
  return ACCENT_TEXT_COLOR_DEFINITIONS.reduce(
    (record, definition) => {
      record[definition.value] = select(definition);
      return record;
    },
    {} as Record<AccentTextColor, string>,
  );
}

const ACCENT_TEXT_COLOR_CLASS = recordByAccentColor(
  (definition) => definition.className,
);

const ACCENT_TEXT_COLOR_HEX = recordByAccentColor(
  (definition) => definition.hex,
);

const ACCENT_TEXT_ANIMATION_OPTIONS = [
  { title: "None", value: "none" },
  { title: "Accent fill", value: "accent-fill" },
] as const;

type AccentTextAnimation =
  (typeof ACCENT_TEXT_ANIMATION_OPTIONS)[number]["value"];

function isAccentTextColor(color: string): color is AccentTextColor {
  return ACCENT_TEXT_COLOR_DEFINITIONS.some(
    (definition) => definition.value === color,
  );
}

function resolveAccentTextColor(
  color: string | null | undefined,
): AccentTextColor {
  if (color && isAccentTextColor(color)) {
    return color;
  }

  return "clear-sky";
}

function isAccentTextFillAnimation(
  animation: string | null | undefined,
): boolean {
  return animation === "accent-fill";
}

export type { AccentTextAnimation, AccentTextColor };

export {
  ACCENT_TEXT_ANIMATION_OPTIONS,
  ACCENT_TEXT_COLOR_CLASS,
  ACCENT_TEXT_COLOR_HEX,
  ACCENT_TEXT_COLOR_OPTIONS,
  isAccentTextFillAnimation,
  resolveAccentTextColor,
};
